import { epgChannelsModel } from '$/epg/channels'
import { api, LiveChannel, type LiveChannelRegion } from '@setplex/tria-api'
import {
  combine,
  createEvent,
  createStore,
  sample,
  split,
  Store,
} from 'effector'
import { CategoryId, CategoryTitle } from '~/entities/epg/categories'
import { model as session } from '~/entities/session'
import { loadMoreEpgChannelsModel } from '~/features/epg/channels/load-more'
import { $route } from '~/processes/navigation'
import { ROUTE_NAME } from '~/shared/constants'
import { epgCategoriesWidgetModel } from '~/widgets/epg/categories'
import { epgChannelsWidgetModel } from '~/widgets/epg/channels'
import { selectEpgCategoryModel } from '../categories/select'
import { selectEpgChannelModel } from '../channels/select'

// Load all channels on Player open or fullscreen enter on EPG-board
export const setCanBeLoaded = createEvent<boolean>()

// It is necessary to put it in false so that loading does not occur on each region/category change
export const $canBeLoadedAllChannels = createStore(false) //
  .on(setCanBeLoaded, (_, bool) => bool)

// Category for Player playlist
export const setPlaylistCategory = createEvent<{
  id: number | CategoryId | null
  title: string | null
} | null>()

export const resetPlaylistCategoryToAll = createEvent()

export const $playlistCategory = createStore<{
  id: number | CategoryId | null
  title: string | null
} | null>(null) //
  .on(setPlaylistCategory, (_, category) => category)
  .on(resetPlaylistCategoryToAll, () => ({
    id: CategoryId.All,
    title: CategoryTitle.All,
  }))

/* If the session ends and 'My list' playlist category was selected => navigate to All tab,
set playlist category to All and reset channel
also should be created page_open and epg_category events */
// clocked on `sessionLost`, not on `session.$isAuthenticated`: that store also flips to `false`
// when a guest session resolves, which would bounce a guest off the My List playlist set from the url
sample({
  clock: session.sessionLost,
  source: $playlistCategory,
  filter: (playlistCategory) => playlistCategory?.id === CategoryId.Favorite,
  target: [
    resetPlaylistCategoryToAll,
    epgCategoriesWidgetModel.navigateToAllCategory,
    selectEpgChannelModel.resetChannel,
  ],
})

// Region for Player playlist
export const setPlaylistRegion = createEvent<LiveChannelRegion | null>()

export const $playlistRegion = createStore<LiveChannelRegion | null>(null, {
  updateFilter: (update, current) => {
    return update?.id !== current?.id && update?.name !== current?.name
  },
}).on(setPlaylistRegion, (_, region) => region)

export const $playerPlaylist: Store<LiveChannel[]> = combine(
  $playlistCategory,
  epgChannelsModel.$byCategory,
  (playlistCategory, [channels]) => {
    if (playlistCategory?.id == null) return []
    return channels.get(playlistCategory.id) ?? []
  }
)

// load more (load all) channels by player playlist category
// (loading it gradually, because there are many other important requests that also need to happen)
sample({
  clock: [
    $playerPlaylist,
    $playlistCategory,
    $playlistRegion,
    $canBeLoadedAllChannels,
  ],
  source: {
    category: $playlistCategory,
    region: $playlistRegion,
    canBeLoaded: $canBeLoadedAllChannels,
  },
  filter: ({ category, region, canBeLoaded }) => {
    return category?.id != null && region?.id != null && canBeLoaded
  },
  fn: ({ category, region }) => ({
    categoryId: category?.id,
    regionId: region?.id,
  }),
  target: loadMoreEpgChannelsModel.load,
})

/* Logic of favorites */
export const handleFavoriteChannelInPlayerBtnClick = createEvent<{
  channel: LiveChannel
  isAdd: boolean
}>()

const removeChannelFromFavorite = createEvent<{
  channel: LiveChannel
}>()

//reload favorite channels if it was added new one
const updateFavoriteChannels = createEvent()

export const checkFavoriteLogic = createEvent<{
  channel: LiveChannel
  isAdd: boolean
}>()

sample({
  clock: checkFavoriteLogic,
  source: $route,
  filter: (route) => {
    const isFullscreen =
      // @ts-expect-error TS2551 because Property 'webkitFullscreenElement' does not exist on type 'Document' this fix only Safari #OVP-2982
      Boolean(document.webkitFullscreenElement) ||
      Boolean(document.fullscreenElement)
    return route?.routeName === ROUTE_NAME.LIVE_CATEGORIES && isFullscreen
  },
  fn: (_, { channel, isAdd }) => ({ channel, isAdd }),
  target: handleFavoriteChannelInPlayerBtnClick,
})

split({
  source: handleFavoriteChannelInPlayerBtnClick,
  match: {
    updateFavoriteChannels: ({ isAdd }) => isAdd,
    removeChannelFromFavorite: ({ isAdd }) => !isAdd,
  },
  cases: {
    updateFavoriteChannels,
    removeChannelFromFavorite,
  },
})

// filter channels in byCategory map Favorite category, if remove channel from favorites
// listen only when handle favorite btn in player (not api.live.base.removeFromFavoritesFx) to prevent hot changes at epg board
sample({
  clock: removeChannelFromFavorite,
  source: epgChannelsModel.$byCategory,
  fn: ([channels, meta], { channel }) => {
    const channelId = channel.id

    const favoriteChannels = channels.get(CategoryId.Favorite)
    if (favoriteChannels) {
      const newFavoriteChannels = favoriteChannels.filter(
        (channel) => channel.id !== channelId
      )
      channels.set(CategoryId.Favorite, newFavoriteChannels)
    }

    return [channels, meta] as const
  },
  target: epgChannelsModel.$byCategory,
})

// if was added new favorite channel, we need to reset category
// listen only when handle favorite btn in player
sample({
  clock: updateFavoriteChannels,
  target: epgChannelsModel.resetFavoriteCategory,
})

// reload shoud be only after api.live.base.addToFavoritesFx.pending is resolved and if we are in favorite category
// to show updated list of favorite channels in sidebar
sample({
  clock: [updateFavoriteChannels, api.live.base.addToFavoritesFx.pending],
  source: {
    categoryId: selectEpgCategoryModel.$activeId,
    pending: api.live.base.addToFavoritesFx.pending,
  },
  filter: ({ categoryId, pending }) => {
    return categoryId === CategoryId.Favorite && !pending
  },
  target: epgChannelsWidgetModel.reloadFavoritesChannels,
})

export const openPlayerFullscreen = createEvent()

// as we shoud not hide removed channel from epg board or from My list grid, but have to hide it in player,
// we need to reset favorite category and reload favorites channels when open fullscreen/select channel in grid
sample({
  clock: openPlayerFullscreen,
  source: {
    categoryId: selectEpgCategoryModel.$activeId,
    favChannelsShouldBeLoadedAgain:
      epgChannelsModel.$favChannelsShouldBeLoadedAgain,
  },
  filter: ({ categoryId, favChannelsShouldBeLoadedAgain }) => {
    return categoryId === CategoryId.Favorite && favChannelsShouldBeLoadedAgain
  },
  target: [
    epgChannelsModel.resetFavoriteCategory,
    epgChannelsWidgetModel.reloadFavoritesChannels,
  ],
})
